import {
    InteractionRequiredAuthError,
    type AccountInfo,
    type IPublicClientApplication,
} from '@azure/msal-browser'

export const powerBiTokenRequest = {
    scopes: ['https://analysis.windows.net/powerbi/api/Report.Read.All'],
}

type PowerBiAuthOptions = {
    instance: IPublicClientApplication
    account?: AccountInfo
    accessToken: string
    isTeams: boolean
    isEmbedded: boolean
    interactive: boolean
    getTeamsToken: () => Promise<string>
}

/** Returns null when the report must show a sign-in button (or is redirecting). */
export async function acquirePowerBiAccessToken({
    instance, account, accessToken, isTeams, isEmbedded, interactive, getTeamsToken,
}: PowerBiAuthOptions): Promise<string | null> {
    // Teams returns a token without populating the tab's MSAL account cache.
    // Once we have it, an empty account list must never start another login.
    if (accessToken) return accessToken

    if (isTeams) {
        // Teams authentication must originate from a user gesture, not an effect.
        return interactive ? requireAccessToken(await getTeamsToken()) : null
    }

    const request = { ...powerBiTokenRequest, account }
    if (account && !interactive) {
        try {
            const response = await instance.acquireTokenSilent({ ...request, account })
            return requireAccessToken(response.accessToken)
        } catch (error) {
            if (!(error instanceof InteractionRequiredAuthError)) throw error
        }
    }

    if (isEmbedded) {
        if (!interactive) return null
        const response = account
            ? await instance.acquireTokenPopup(request)
            : await instance.loginPopup(request)
        if (response.account) instance.setActiveAccount(response.account)
        return requireAccessToken(response.accessToken)
    }

    if (account) {
        await instance.acquireTokenRedirect(request)
    } else {
        await instance.loginRedirect(request)
    }
    return null
}

function requireAccessToken(token: string): string {
    if (!token) throw new Error('Microsoft did not return a Power BI access token.')
    return token
}
